/**
 * Permission key constants for the key-based permission system.
 * Keys follow the format: {module_key}.{action}
 *
 * This is the single source of truth — never use raw strings in components.
 * Import and use: PERMISSIONS.STAFF_DIRECTORY.VIEW
 */
export const PERMISSIONS = {
  // Staff Directory (ModuleId: 3)
  STAFF_DIRECTORY: {
    VIEW: 'staff_directory.view',
    ADD: 'staff_directory.add',
    EDIT: 'staff_directory.edit',
    DELETE: 'staff_directory.delete',
  },

  // Leave Management (ModuleId: 5)
  LEAVE_MANAGEMENT: {
    VIEW: 'leave_management.view',
    ADD: 'leave_management.add',
    EDIT: 'leave_management.edit',
    DELETE: 'leave_management.delete',
    APPROVE: 'leave_management.approve_reject',
  },

  // Department Teams (ModuleId: 6)
  DEPARTMENT_TEAMS: {
    VIEW: 'department_teams.view',
    ADD: 'department_teams.add',
    EDIT: 'department_teams.edit',
    DELETE: 'department_teams.delete',
    ADD_EDIT_TEAM: 'department_teams.add_edit_team',
    DELETE_TEAM: 'department_teams.delete_team',
  },

  // Attendance (ModuleId: 7)
  ATTENDANCE: {
    VIEW: 'attendance.view',
    APPROVE: 'attendance.approve_reject',
    REGULARIZATION: 'attendance.regularization',
  },

  // Shift Scheduling (ModuleId: 8)
  SHIFT_SCHEDULING: {
    VIEW: 'shift_scheduling.view',
    BUILD: 'shift_scheduling.build_shift',
    PUBLISH: 'shift_scheduling.publish',
    ADD: 'shift_scheduling.add',
    EDIT: 'shift_scheduling.edit',
    DELETE: 'shift_scheduling.delete',
  },

  // Template (ModuleId: 29)
  TEMPLATE: {
    VIEW: 'template.view',
    ADD_FOLDER: 'template.add_folder',
    SIGNATURE_SETUP: 'template.signature_setup',
    TEMPLATE_ASSIGN: 'template.template_assign',
    UPLOAD: 'template.upload_documents',
  },

  // Profile - User Setting (ModuleId: 31)
  PROFILE_USER_SETTING: {
    VIEW: 'profile_user_setting.view',
    EDIT: 'profile_user_setting.edit',
  },

  // Profile - Job Setting (ModuleId: 32)
  PROFILE_JOB_SETTING: {
    VIEW: 'profile_job_setting.view',
  },

  // Profile - Income Setting (ModuleId: 33)
  PROFILE_INCOME_SETTING: {
    VIEW: 'profile_income_setting.view',
  },

  // Profile - Attendance (ModuleId: 34)
  PROFILE_ATTENDANCE: {
    VIEW: 'profile_attendance.view',
    REGULARIZATION: 'profile_attendance.regularization',
  },

  // Profile - Shift (ModuleId: 39)
  PROFILE_SHIFT: {
    VIEW: 'profile_shift.view',
  },

  // Profile - Leave (ModuleId: 35)
  PROFILE_LEAVE: {
    VIEW: 'profile_leave.view',
    ADD: 'profile_leave.add',
    EDIT: 'profile_leave.edit',
    DELETE: 'profile_leave.delete',
  },

  // Profile - Clock-ins (ModuleId: 36)
  PROFILE_CLOCK_INS: {
    VIEW: 'profile_clock_ins.view',
  },

  // Profile - Documents (ModuleId: 37)
  PROFILE_DOCUMENTS: {
    VIEW: 'profile_documents.view',
    UPLOAD: 'profile_documents.upload_documents',
  },

  // Profile - Financial (ModuleId: 42)
  PROFILE_FINANCIAL: {
    VIEW: 'profile_financial_reports.view',
  },
} as const;

/** Type helper: extracts all permission string literal values */
type PermissionValues<T> =
  T extends Record<string, infer V> ? (V extends string ? V : PermissionValues<V>) : never;

export type PermissionKey = PermissionValues<typeof PERMISSIONS>;
